import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import LeftAccentCard from '../components/LeftAccentCard';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import TextField from '@mui/material/TextField';
import TimeSelect from '../components/common/TimeSelect';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import PageHeader from '../components/PageHeader';
import { ReactComponent as RecruitFabIcon } from '../assets/icons/recruit-fab.svg';
import { ReactComponent as SidebarJobMgmtIcon } from '../assets/icons/sidebar_job_management.svg';
import SvgIcon from '@mui/material/SvgIcon';
import DeleteIcon from '@mui/icons-material/Delete';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Popover from '@mui/material/Popover';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import LabelIcon from '@mui/icons-material/Label';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';

// API関連のインポート
import { jobPostingApi, lineApi, storeApi, jobPositionApi, jobShiftApi, smokingPreventionApi, skillSettingsApi, broadcastApi, getApiErrorMessage } from '../services/api';
import { JobPostingTemplate, JobPostingTemplateCreate, LineMessageSend, JobPositionWithIndustry, JobShiftCreate, SmokingPreventionMeasure, SkillTier, BroadcastTargetMode, BroadcastByRankRequest } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import RankStar from '../components/RankStar';
import { calcTotalPayment } from '../utils/salaryUtils';

import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// JobPostingTemplateCreateを拡張した型
interface ExtendedJobPostingTemplateCreate extends JobPostingTemplateCreate {
  store_id: string | null;
  position_id?: string;
  salary?: string;
  admin_label?: string;
}

// 日本式日付ピッカーコンポーネント
interface JapaneseDatePickerProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const JapaneseDatePicker: React.FC<JapaneseDatePickerProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState(
    value ? new Date(value.getFullYear(), value.getMonth(), 1) : new Date()
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // 令和年を計算する関数
  const getRewaYear = (year: number): number => {
    return year - 2018; // 2019年が令和元年
  };

  // 月の日数を取得
  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // 月の最初の日の曜日を取得
  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // 今日の日付
  const today = new Date();
  const isToday = (date: Date): boolean => {
    return date.toDateString() === today.toDateString();
  };

  // 日付が選択されているかチェック
  const isSelected = (date: Date): boolean => {
    return value ? date.toDateString() === value.toDateString() : false;
  };

  // 前月に移動
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // 次月に移動
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // 日付を選択
  const selectDate = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onChange(selectedDate);
    handleClose();
  };

  // 削除
  const clearDate = () => {
    onChange(null);
    handleClose();
  };

  // 今日を選択
  const selectToday = () => {
    const todayDate = new Date(today);
    onChange(todayDate);
    setCurrentMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
    handleClose();
  };

  // カレンダーを描画
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // 空のセル（前月の末尾）
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box key={`empty-${i}`} sx={{ width: 32, height: 32 }} />
      );
    }

    // 日付のセル
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayOfWeek = date.getDay();
      const isTodayDate = isToday(date);
      const isSelectedDate = isSelected(date);

      let textColor = '#000000';
      if (dayOfWeek === 0) textColor = '#f44336'; // 日曜日（赤）
      if (dayOfWeek === 6) textColor = '#2196f3'; // 土曜日（青）

      days.push(
        <Box
          key={day}
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: textColor,
            backgroundColor: isSelectedDate ? '#2196f3' : isTodayDate ? '#e0e0e0' : 'transparent',
            ...(isSelectedDate && { color: '#ffffff' }),
            '&:hover': {
              backgroundColor: isSelectedDate ? '#1976d2' : '#e3f2fd',
              color: isSelectedDate ? '#ffffff' : '#1976d2',
            },
          }}
          onClick={() => selectDate(day)}
        >
          {day}
        </Box>
      );
    }

    return days;
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1;
  const rewaYear = getRewaYear(year);

  return (
    <>
      <TextField
        label={label}
        value={value ? format(value, 'yyyy/MM/dd') : ''}
        onClick={handleClick}
        InputProps={{
          readOnly: true,
        }}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
        sx={{ cursor: disabled ? 'default' : 'pointer' }}
      />
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          {/* カレンダーヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {year}年（令和{rewaYear}年）{month}月
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={goToPreviousMonth}
                sx={{ 
                  p: 1,
                  minWidth: 36,
                  minHeight: 36,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Typography sx={{ fontSize: '1.2rem', color: '#666', fontWeight: 'bold' }}>↑</Typography>
              </IconButton>
              <IconButton
                size="small"
                onClick={goToNextMonth}
                sx={{ 
                  p: 1,
                  minWidth: 36,
                  minHeight: 36,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <Typography sx={{ fontSize: '1.2rem', color: '#666', fontWeight: 'bold' }}>↓</Typography>
              </IconButton>
            </Box>
          </Box>

          {/* 曜日ヘッダー */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  py: 0.5,
                  color: index === 0 ? '#f44336' : index === 6 ? '#2196f3' : '#000000',
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* カレンダーグリッド */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
            {renderCalendar()}
          </Box>

          {/* フッターボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              size="small"
              onClick={clearDate}
              sx={{ color: '#2196f3' }}
            >
              削除
            </Button>
            <Button
              size="small"
              onClick={selectToday}
              sx={{ color: '#2196f3' }}
            >
              今日
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// 時間オプションを生成する関数（24時間）
const generateHourOptions = (): number[] => {
  return Array.from({ length: 24 }, (_, i) => i);
};

// 分オプションを生成する関数（15分単位）
const generateMinuteOptions = (): number[] => {
  return Array.from({ length: 4 }, (_, i) => i * 15);
};

// 時間文字列から時と分を分離する関数
const parseTime = (timeStr: string): { hour: number; minute: number } => {
  if (!timeStr) return { hour: 0, minute: 0 };
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hour: hours || 0, minute: minutes || 0 };
};

// 時と分から時間文字列を生成する関数
const formatTime = (hour: number, minute: number): string => {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};



// テンプレート作成ダイアログのインターフェース
interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: ExtendedJobPostingTemplateCreate) => void;
  isLoading: boolean;
  error: string | null;
  storeAddress: string;
  jobPositions: JobPositionWithIndustry[];
  loadingJobPositions: boolean;
}

// テンプレート作成ダイアログ
const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
  error,
  storeAddress,
  jobPositions,
  loadingJobPositions
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { storeId } = useAuth();

  const [currentTemplate, setCurrentTemplate] = useState<ExtendedJobPostingTemplateCreate>({
    name: '',
    description: '',
    content: '',
    job_title: '',
    job_type: 'パート・アルバイト',
    address: storeAddress,
    salary_type: 'hourly',
    salary_amount: undefined,
    salary_allowance: '',
    salary_transport: '',
    is_active: true,
    has_line_message: true,
    line_message_template: '',
    store_id: storeId,
    position_id: '',
    salary: '',
    admin_label: ''
  });

  // バリデーションエラーの状態を追加
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // ダイアログが開かれるたびにフォームをリセット
  useEffect(() => {
    if (open) {
      setCurrentTemplate({
        name: '',
        description: '',
        content: '',
        job_title: '',
        job_type: 'パート・アルバイト',
        address: storeAddress,
        salary_type: 'hourly',
        salary_amount: undefined,
        salary_allowance: '',
        salary_transport: '',
        is_active: true,
        has_line_message: true,
        line_message_template: '',
        store_id: storeId,
        position_id: '',
        salary: '',
        admin_label: ''
      });
      // バリデーションエラーもリセット
      setValidationErrors({});
    }
  }, [open, storeAddress, storeId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // バリデーションエラーをクリア
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // 数値フィールドの処理
    if (name === 'salary_amount') {
      const numericOnly = value.replace(/[^\d]/g, '');
      const numberValue = numericOnly === '' ? undefined : parseInt(numericOnly, 10);
      setCurrentTemplate({
        ...currentTemplate,
        [name]: numberValue
      });
    } else {
      setCurrentTemplate({
        ...currentTemplate,
        [name]: value
      });
    }
  };

  // 数値をカンマ区切りでフォーマットする関数
  const formatWithCommas = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null || value === '') return '';
    const num = typeof value === 'number' ? value : parseInt(String(value).replace(/[^\d]/g, ''), 10);
    if (isNaN(num)) return '';
    return num.toLocaleString();
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    
    // バリデーションエラーをクリア
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === 'job_type' || name === 'salary_type') {
      setCurrentTemplate({
        ...currentTemplate,
        [name]: value
      });
      return;
    }
    
    if (name === 'position_id') {
      const selectedPosition = jobPositions.find(position => position.id.toString() === value);
      const jobTitle = selectedPosition ? selectedPosition.name : '';
      
      setCurrentTemplate({
        ...currentTemplate,
        position_id: value,
        job_title: jobTitle
      });
      return;
    }
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setCurrentTemplate({
      ...currentTemplate,
      [name]: checked
    });
  };

  const handleSubmit = () => {
    // 入力バリデーション
    const errors: {[key: string]: string} = {};
    if (!currentTemplate.name?.trim()) {
      errors.name = '募集職種名を入力してください';
    }
    if (!currentTemplate.position_id) {
      errors.position_id = '職種を選択してください';
    }
    if (
      currentTemplate.salary_amount === undefined ||
      currentTemplate.salary_amount === null ||
      Number.isNaN(currentTemplate.salary_amount) ||
      currentTemplate.salary_amount <= 0
    ) {
      errors.salary_amount = currentTemplate.salary_type === 'hourly'
        ? '時給を入力してください'
        : '日給を入力してください';
    }
    if (!currentTemplate.content?.trim()) {
      errors.content = '仕事内容（求人用）を入力してください';
    }
    if (!currentTemplate.line_message_template?.trim()) {
      errors.line_message_template = '配信メッセージを入力してください';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // 給与テキストのフォーマット
    let formattedSalary = '';
    if (currentTemplate.salary_amount) {
      if (currentTemplate.salary_type === 'hourly') {
        formattedSalary = `時給 ${currentTemplate.salary_amount.toLocaleString()}円`;
      } else {
        formattedSalary = `日給 ${currentTemplate.salary_amount.toLocaleString()}円`;
      }
      
      if (currentTemplate.salary_allowance) {
        formattedSalary += ` / 手当: ${currentTemplate.salary_allowance}`;
      }
      
      if (currentTemplate.salary_transport) {
        formattedSalary += ` / 交通費: ${currentTemplate.salary_transport}`;
      }
    } else {
      formattedSalary = '応相談';
    }

    const templateData: ExtendedJobPostingTemplateCreate = {
      ...currentTemplate,
      address: storeAddress,
      salary: formattedSalary
    };

    onSave(templateData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>
        求人ひな形を新規作成
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
          <TextField
            label="募集職種名"
            name="name"
            value={currentTemplate.name}
            onChange={handleInputChange}
            fullWidth
            required
            margin="normal"
            placeholder="例: エンジニア募集"
            error={!!validationErrors.name || error?.includes('募集職種名')}
            helperText={validationErrors.name || ''}
          />
          <TextField
            label="募集メモ（任意）"
            name="admin_label"
            value={currentTemplate.admin_label}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            placeholder="例: 夏季限定、週末シフト用など"
            helperText="※管理画面での識別用のメモです。求職者には表示されません"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LabelIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                required 
                error={!!validationErrors.position_id || error?.includes('職種')}
              >
                <InputLabel id="job-position-label">職種</InputLabel>
                <Select
                  labelId="job-position-label"
                  name="position_id"
                  value={currentTemplate.position_id}
                  label="職種"
                  onChange={handleSelectChange}
                  disabled={loadingJobPositions}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 'auto',
                        minWidth: 280,
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    variant: isMobile ? 'menu' : 'selectedMenu',
                  }}
                >
                  {jobPositions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {loadingJobPositions ? '職種を読み込み中...' : '職種が設定されていません'}
                    </MenuItem>
                  ) : (
                    jobPositions.map((position) => {
                      const industryName = position.industry ? position.industry.name : 'その他';
                      return (
                        <MenuItem 
                          key={position.id} 
                          value={position.id}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            py: 1,
                            minHeight: 'auto',
                          }}
                        >
                          {industryName} / {position.name}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {validationErrors.position_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {validationErrors.position_id}
                  </Typography>
                )}
                {jobPositions.length === 0 && !loadingJobPositions && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, ml: 1 }}>
                    ※ 店舗設定の職種管理で職種を追加してください
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="job-type-label">雇用形態</InputLabel>
                <Select
                  labelId="job-type-label"
                  name="job_type"
                  value={currentTemplate.job_type}
                  label="雇用形態"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="パート・アルバイト">パート・アルバイト</MenuItem>                      
                  <MenuItem value="正社員">正社員</MenuItem>
                  <MenuItem value="契約社員">契約社員</MenuItem>
                  <MenuItem value="業務委託">業務委託</MenuItem>
                  <MenuItem value="派遣社員">派遣社員</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="勤務地"
                name="address"
                value={storeAddress || "勤務地情報が取得できませんでした"}
                fullWidth
                required
                disabled={true}
                InputProps={{
                  readOnly: true,
                }}
                helperText="勤務地は店舗情報から自動取得され変更できません"
                error={!storeAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="salary-type-label">給与タイプ</InputLabel>
                <Select
                  labelId="salary-type-label"
                  name="salary_type"
                  value={currentTemplate.salary_type}
                  label="給与タイプ"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="hourly">時給</MenuItem>
                  <MenuItem value="daily">日給</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label={currentTemplate.salary_type === 'hourly' ? '時給（円）' : '日給（円）'}
                name="salary_amount"
                value={formatWithCommas(currentTemplate.salary_amount)}
                onChange={handleInputChange}
                fullWidth
                required
                type="text"
                error={!!validationErrors.salary_amount}
                helperText={validationErrors.salary_amount || ''}
                InputProps={{
                  inputProps: { inputMode: 'numeric' },
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                }}
                placeholder="例: 1,200"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="手当"
                name="salary_allowance"
                value={currentTemplate.salary_allowance}
                onChange={handleInputChange}
                fullWidth
                placeholder="例: 1000円、昇給あり、など"
                helperText="数値のみの場合は自動で「円」が付きます"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="交通費"
                name="salary_transport"
                value={currentTemplate.salary_transport}
                onChange={handleInputChange}
                fullWidth
                placeholder="例: 500円、全額支給、など"
                helperText="数値のみの場合は自動で「円」が付きます"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="仕事内容（求人用）"
                name="content"
                value={currentTemplate.content}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={8}
                placeholder="求職者向けの仕事内容を記載してください。LINE配信やWEB掲載時に使用されます。"
                error={!!validationErrors.content || error?.includes('仕事内容（求人用）')}
                helperText={validationErrors.content || ''}
                required
              />
            </Grid>
          </Grid>
          
          <TextField
            label="業務内容（労働条件通知書用）"
            name="description"
            value={currentTemplate.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            margin="normal"
            placeholder="労働条件通知書に記載する詳細な業務内容"
            helperText="※労働条件通知書などの公式書類で使用される業務内容です"
          />
          
          <TextField
            label="配信メッセージ"
            name="line_message_template"
            value={currentTemplate.line_message_template}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={6}
            margin="normal"
            placeholder="LINE配信時に使用するメッセージを記載してください。"
            error={!!validationErrors.line_message_template || error?.includes('配信メッセージ')}
            helperText={validationErrors.line_message_template || '※今すぐ募集で配信されるメッセージです'}
            required
          />
          
          <Box sx={{ display: 'flex', mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentTemplate.is_active}
                  onChange={(e) => handleSwitchChange('is_active', e.target.checked)}
                  color="success"
                />
              }
              label="有効にする"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          キャンセル
        </Button>
        <Button 
          onClick={handleSubmit} 
          sx={{ bgcolor: '#0071e3', '&:hover': { bgcolor: '#0062c4' } }}
          variant="contained"
          disabled={isLoading || loadingJobPositions}
        >
          {isLoading ? <CircularProgress size={24} /> : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const formatWithCommasBroadcast = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'number' ? value : parseInt(String(value).replace(/[^\d]/g, ''), 10);
  if (isNaN(num)) return '';
  return num.toLocaleString();
};

const LineBroadcast: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialTemplateId = searchParams.get('template');
  const { storeId } = useAuth(); // AuthContextから店舗IDを取得
  const { managementToken } = useManagementAuth();
  // ProtectedRoutes 配下で管理トークンは Layout の自動ログインで設定される（CrewManagement と同パターン）。
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [jobTemplates, setJobTemplates] = useState<JobPostingTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<JobPostingTemplate | null>(null);
  // 選択中テンプレートの「配信可能な既存アクティブシフト数」（本日以降・募集枠未充足のもの）。
  // この後さらに新規シフトを1件作って配信するため、>= 1 ならば配信時には複数件扱いになり、
  // バックエンドが「他にも募集シフトがありますのでサイトでご確認ください」を挿入する。
  const [existingActiveShiftsCount, setExistingActiveShiftsCount] = useState<number>(0);
  const [messageContent, setMessageContent] = useState<string>('');
  const [targetSegment, setTargetSegment] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // 入力エラー箇所までスクロール＆フォーカス
  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 100);
  };

  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
  };

  const [segmentCounts, setSegmentCounts] = useState<Record<string, number>>({
    all: 0,
    rankA_users: 0,
    B_plus_users: 0,
  });

  // ─── 配信対象の指定方式（従来セグメント or スキルランク別） ───────────────
  // 'segment': 従来の lineApi.sendMessage（all/rankA_users/B_plus_users）
  // 'rank':    スキルランク別配信（broadcastApi.byRank。tier_ids + target_mode）
  const [targetingMode, setTargetingMode] = useState<'segment' | 'rank'>('segment');
  // スキルランク tier マスタ（rookie..gold）
  const [skillTiers, setSkillTiers] = useState<SkillTier[]>([]);
  // 選択中の tier_id（チェックボックス）
  const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
  // 配信モード: tier（完全一致 / 全クルー=all は別トグル）/ tier_and_above（包含 >=）
  const [rankTargetMode, setRankTargetMode] = useState<BroadcastTargetMode>('tier_and_above');
  // 対象人数プレビュー（ランク別）
  const [rankTargetCount, setRankTargetCount] = useState<number | null>(null);
  const [loadingRankCount, setLoadingRankCount] = useState<boolean>(false);
  // 各スキルランク別の個別人数
  const [tierCounts, setTierCounts] = useState<Record<string, number>>({});
  const [loadingTierCounts, setLoadingTierCounts] = useState<boolean>(false);
  
  // 勤務日・時間・仕事内容のstate
  const [workDate, setWorkDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('18:00');
  const [endTime, setEndTime] = useState<string>('22:00');
  const [requiredWorkers, setRequiredWorkers] = useState<string>('1');
  const [breakDurationMinutes, setBreakDurationMinutes] = useState<string>('0');
  const [jobContent, setJobContent] = useState<string>('');
  const [workDescription, setWorkDescription] = useState<string>('');

  // 給与・手当のstate
  const [broadcastSalaryAmount, setBroadcastSalaryAmount] = useState<string>('');
  const [broadcastAllowance, setBroadcastAllowance] = useState<string>('');
  const [broadcastTransport, setBroadcastTransport] = useState<string>('');

  // LINE配信メッセージの動的定型文プレビュー（フォーム値から自動生成）
  const broadcastPreview = useMemo(() => {
    const jobTitle = selectedTemplate?.job_title || '';
    if (!jobTitle) return '';

    // 報酬計算
    const total = calcTotalPayment(
      parseInt(broadcastSalaryAmount, 10) || undefined,
      selectedTemplate?.salary_type,
      startTime,
      endTime,
      parseInt(breakDurationMinutes, 10) || 0,
      broadcastAllowance,
      broadcastTransport
    );
    const rewardText = total !== null ? `${total.toLocaleString()}円` : '要相談';

    // 勤務日フォーマット
    let dateText = '未設定';
    if (workDate) {
      try {
        const d = parseISO(workDate);
        dateText = format(d, 'M/d（E）', { locale: ja });
      } catch { dateText = workDate; }
    }

    // 時間フォーマット ("18:00" → "18時", "17:15" → "17時15分")
    const fmtHour = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return m ? `${h}時${m}分` : `${h}時`;
    };
    const timeText = `${fmtHour(startTime)}〜${fmtHour(endTime)}`;

    // 募集人数
    const workers = parseInt(requiredWorkers, 10) || 1;

    return `【${jobTitle}募集】\n🌟報酬　${rewardText}\n🌟${dateText}　${timeText}\n🌟募集人数　${workers}名`;
  }, [selectedTemplate?.job_title, selectedTemplate?.salary_type, broadcastSalaryAmount, startTime, endTime, breakDurationMinutes, broadcastAllowance, broadcastTransport, workDate, requiredWorkers]);

  // バックエンドが複数シフト時に挿入するシステム案内文。
  // ⚠️ 同期注意: nity-recrew-backend/app/api/line/broadcast.py の `additional_message` と同一文言を保つこと。
  // 文言を変更する場合は必ず両ファイルを同時に更新する（不一致だとプレビューと実配信がズレる）。
  const MULTI_SHIFTS_NOTICE = '他にも募集シフトがありますのでサイトでご確認ください';

  // 配信時の最終メッセージプレビュー（実配信と同じ順序で結合）
  // 順序: 動的定型文 → (今これから1件追加するため、既存アクティブが1件以上あれば配信時は複数件) システム案内 → 手動入力テキスト
  const fullBroadcastPreview = useMemo(() => {
    if (!broadcastPreview) return '';
    const willBeMultipleShifts = existingActiveShiftsCount >= 1;
    const parts: string[] = [broadcastPreview];
    if (willBeMultipleShifts) parts.push(MULTI_SHIFTS_NOTICE);
    const manual = messageContent.trim();
    if (manual) parts.push(manual);
    return parts.join('\n\n');
  }, [broadcastPreview, existingActiveShiftsCount, messageContent]);

  // トースト通知用のstate
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

  // テンプレート作成関連のstate
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState<boolean>(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // 店舗情報の状態
  const [storeAddress, setStoreAddress] = useState<string>("");
  const [loadingStoreInfo, setLoadingStoreInfo] = useState<boolean>(true);

  // 職種情報の状態（業種情報を含む）
  const [jobPositions, setJobPositions] = useState<JobPositionWithIndustry[]>([]);
  const [loadingJobPositions, setLoadingJobPositions] = useState<boolean>(true);

  // 受動喫煙防止措置の状態
  const [smokingMeasures, setSmokingMeasures] = useState<SmokingPreventionMeasure[]>([]);
  const [selectedSmokingMeasureId, setSelectedSmokingMeasureId] = useState<string>('');

  // 受動喫煙防止措置マスタを取得
  useEffect(() => {
    const fetchSmokingMeasures = async () => {
      try {
        const measures = await smokingPreventionApi.getActive();
        setSmokingMeasures(measures);
      } catch (err) {
        // 任意項目のため、エラー時は空配列のまま継続
      }
    };
    fetchSmokingMeasures();
  }, []);

  // スキルランク tier マスタを取得（ランク別配信のチェックボックス用）
  useEffect(() => {
    const fetchTiers = async () => {
      if (!storeId) {
        setSkillTiers([]);
        return;
      }
      try {
        const tiers = await skillSettingsApi.getSkillTiers(storeId, token);
        // sort_order 昇順（rookie..gold）に整列
        setSkillTiers([...tiers].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      } catch (err) {
        // ゲーミフィケーション無効時などは空配列のまま継続（ランク別UIは出るが選択肢なし）
        setSkillTiers([]);
      }
    };
    fetchTiers();
  }, [storeId, token]);

  // ランク別配信の対象指定オブジェクトを組み立てる（target_mode + tier_ids）。
  // 'tier_and_above' は選択 tier の最小 sort_order 以上を包含（>=）。サーバが min_tier_sort_order に展開。
  const buildRankTarget = useCallback((): { target_mode: BroadcastTargetMode; tier_ids: string[] } => {
    if (rankTargetMode === 'all') {
      return { target_mode: 'all', tier_ids: [] };
    }
    return { target_mode: rankTargetMode, tier_ids: selectedTierIds };
  }, [rankTargetMode, selectedTierIds]);

  // 対象人数プレビュー（ランク別）。選択変更のたびに GET /api/line/broadcast/by-rank/count で取得。
  useEffect(() => {
    if (targetingMode !== 'rank' || !storeId) {
      return;
    }
    const target = buildRankTarget();
    // all 以外で tier 未選択なら 0 件確定（API を叩かない）
    if (target.target_mode !== 'all' && target.tier_ids.length === 0) {
      setRankTargetCount(0);
      setLoadingRankCount(false);
      return;
    }
    let cancelled = false;
    setLoadingRankCount(true);
    const timer = setTimeout(async () => {
      try {
        const res = await broadcastApi.byRankCount(
          { store_id: storeId, target_mode: target.target_mode, tier_ids: target.tier_ids },
          token
        );
        if (!cancelled) setRankTargetCount(res.count);
      } catch (err) {
        if (!cancelled) setRankTargetCount(null);
      } finally {
        if (!cancelled) setLoadingRankCount(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [targetingMode, storeId, token, buildRankTarget]);

  // 各スキルランクの個別配信対象人数を取得
  useEffect(() => {
    if (!storeId || skillTiers.length === 0) return;
    let cancelled = false;
    setLoadingTierCounts(true);
    (async () => {
      const counts: Record<string, number> = {};
      await Promise.all(
        skillTiers.map(async (tier) => {
          try {
            const res = await broadcastApi.byRankCount(
              { store_id: storeId, target_mode: 'tier', tier_ids: [tier.tier_id] },
              token
            );
            counts[tier.tier_id] = res.count;
          } catch {
            counts[tier.tier_id] = 0;
          }
        })
      );
      if (!cancelled) {
        setTierCounts(counts);
        setLoadingTierCounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storeId, token, skillTiers]);

  // tier チェックボックスのトグル
  const toggleTierId = (tierId: string) => {
    setSelectedTierIds((prev) =>
      prev.includes(tierId) ? prev.filter((id) => id !== tierId) : [...prev, tierId]
    );
  };

  // 職種情報を取得（業種情報を含む）
  useEffect(() => {
    const fetchJobPositions = async () => {
      setLoadingJobPositions(true);
      try {
        if (!!storeId) {
          const positions = await jobPositionApi.getActiveWithIndustry(storeId);
          setJobPositions(positions);
        } else {
          setJobPositions([]);
        }
      } catch (err: any) {
        setJobPositions([]);
      } finally {
        setLoadingJobPositions(false);
      }
    };

    fetchJobPositions();
  }, [storeId]);

  // 店舗情報を取得
  useEffect(() => {
    const fetchStoreInfo = async () => {
      setLoadingStoreInfo(true);
      try {
        if (!!storeId) {
          const storeInfo = await storeApi.getStoreInfo(storeId);
          setStoreAddress(storeInfo.address);
          setError(null);
        } else {
          setStoreAddress("東京都渋谷区恵比寿1-1-1");
        }
      } catch (err: any) {
        setError('店舗情報の取得に失敗しました。デフォルトの勤務地を使用します。');
        setStoreAddress("東京都渋谷区恵比寿1-1-1");
      } finally {
        setLoadingStoreInfo(false);
      }
    };

    fetchStoreInfo();
  }, [storeId]);

  useEffect(() => {
    // storeIdが存在しない場合は処理を実行しない
    if (!storeId) {
      setIsLoading(false);
      return;
    }

    // APIからデータを取得
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 求人テンプレートの取得 - has_line_messageパラメータを削除
        const jobTemplatesData = await jobPostingApi.getAll({}, storeId);
        setJobTemplates(jobTemplatesData);

        // 各セグメントのユーザー数を取得
        const segments = ['all', 'rankA_users', 'B_plus_users'];
        const counts: Record<string, number> = {};
        
        for (const segment of segments) {
          const result = await lineApi.getSegmentCount(storeId, segment);
          counts[segment] = result.count;
        }
        
        setSegmentCounts(counts);
        
        // URLパラメータからテンプレートIDが指定されていれば、そのひな形を選択
        if (initialTemplateId) {
          const template = jobTemplatesData.find(t => t.id === initialTemplateId); // initialTemplateIdは既にstring
          if (template) {
            // APIから詳細データを取得
            const fullTemplate = await jobPostingApi.getById(template.id, storeId);
            await selectTemplateAndSetMessage(fullTemplate);
          }
        }
        
        setError(null);
      } catch (err: any) {
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    if (!loadingStoreInfo) {
      fetchData();
    }
  }, [initialTemplateId, storeId, loadingStoreInfo]);

  // SelectChangeEvent を受け取るバージョン（UIからの選択用）
  const handleTemplateSelectChange = async (event: SelectChangeEvent) => {
    const templateId: string = event.target.value; // 型を明示的に指定
    const template = jobTemplates.find(t => t.id === templateId);
    if (template && storeId) {
      // APIから最新のテンプレートデータを取得
      const fullTemplate = await jobPostingApi.getById(template.id, storeId);
      await selectTemplateAndSetMessage(fullTemplate);
    }
  };

  // テンプレートオブジェクトを直接受け取るバージョン（プログラムからの選択用）
  const selectTemplateAndSetMessage = async (template: JobPostingTemplate) => {
    setSelectedTemplate(template);
    setJobContent(template.content || '');
    setWorkDescription(template.description || '');
    setBroadcastSalaryAmount(template.salary_amount ? String(template.salary_amount) : '');
    // 手当・交通費は空欄のままシフトを作るとNULL＝テンプレ値フォールバックが効いて
    // 「消したのに以前の値が表示され続ける」状態になるため、未設定時は 0 を明示する
    setBroadcastAllowance(template.salary_allowance || '0');
    setBroadcastTransport(template.salary_transport || '0');

    // 配信可能な既存アクティブシフトの件数を取得（プレビューでの「他にも〜」案内表示判定用）。
    // バックエンド側のフィルタ条件（is_active && shift_date >= today && current_applicants < required_workers）と揃える。
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { shifts } = await jobShiftApi.getAll({ template_id: template.id, store_id: storeId });
      const activeCount = (shifts || []).filter((s) => {
        if (!s.is_active) return false;
        const shiftDate = parseISO(s.shift_date);
        if (shiftDate < today) return false;
        return s.current_applicants < s.required_workers;
      }).length;
      setExistingActiveShiftsCount(activeCount);
    } catch {
      // 取得失敗時は 0 件として扱う（プレビューでの案内表示が出ないだけ）
      setExistingActiveShiftsCount(0);
    }
  
    // テンプレートのプレースホルダーを実際の値に置換
    if (template.broadcast_message) {
      // 新しい配信メッセージフィールドを優先使用
      let message = template.broadcast_message;
      
      // プレースホルダーの置換
      message = message
        .replace(/{job_title}/g, template.job_title || '')
        .replace(/{address}/g, template.address || '')
        .replace(/{salary}/g, template.salary || '要相談')
        .replace(/{company_name}/g, '株式会社サンプル');  // 実際には環境変数などから取得

      setMessageContent(message);
    } else if (template.line_message_template) {
      // 配信メッセージがない場合は従来のline_message_templateを使用
      let message = template.line_message_template;
      
      // プレースホルダーの置換
      message = message
        .replace(/{job_title}/g, template.job_title || '')
        .replace(/{address}/g, template.address || '')
        .replace(/{salary}/g, template.salary || '要相談')
        .replace(/{company_name}/g, '株式会社サンプル');  // 実際には環境変数などから取得
    
      setMessageContent(message);
    } else {
      // どちらもない場合は基本的なメッセージを生成
      const basicMessage = `${template.job_title}\n\n勤務地: ${template.address}\n給与: ${template.salary || '要相談'}\n\n詳細は下記をご確認ください。`;
      setMessageContent(basicMessage);
    }
  };

  const handleTargetSegmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTargetSegment(event.target.value);
  };
  
  // トースト通知を閉じる関数
  const handleCloseToast = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  // テンプレート作成のハンドラ
  const handleCreateTemplate = () => {
    navigate('/job-templates?create=true');
  };

  // テンプレート保存のハンドラ
  const handleSaveTemplate = async (templateData: ExtendedJobPostingTemplateCreate) => {
    // 入力バリデーション
    if (!templateData.name.trim()) {
      setTemplateError('募集職種名は必須です');
      return;
    }
    
    if (!templateData.job_title.trim()) {
      setTemplateError('職種は必須です');
      return;
    }

    if (!templateData.content?.trim()) {
      setTemplateError('仕事内容（求人用）は必須です');
      return;
    }

    if (!templateData.line_message_template?.trim()) {
      setTemplateError('配信メッセージは必須です');
      return;
    }
    
    setTemplateError(null);
    setIsCreatingTemplate(true);

    try {
      const result = await jobPostingApi.create(templateData, storeId);
      
      // 新しく作成されたテンプレートを勤務地を最新情報に更新してリストに追加
      const displayTemplate = {
        ...result,
        address: storeAddress || "東京都渋谷区恵比寿1-1-1"
      };
      
      setJobTemplates([...jobTemplates, displayTemplate]);
      
      // 作成したテンプレートを自動選択
      await selectTemplateAndSetMessage(displayTemplate);
      setShowTemplateDialog(false);
      
      // 成功メッセージ
      setToastMessage('求人ひな形が作成されました');
      setToastSeverity('success');
      setOpenToast(true);
  
    } catch (err: any) {
      // 生の detail を連結せず getApiErrorMessage を通す（5xx / 内部情報はフォールバック）
      setTemplateError(getApiErrorMessage(err, 'ひな形の保存に失敗しました。')); // メイン画面にもエラーを表示
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!storeId) {
      setError('店舗IDが取得できませんでした');
      return;
    }

    // 入力バリデーション（インラインエラー）
    const errors: Record<string, string> = {};
    if (!selectedTemplate) {
      errors.template = 'ひな形を選択してください';
    }
    if (!workDate) {
      errors.workDate = '勤務日を入力してください';
    }
    const parsedRequiredWorkers = Number.parseInt(requiredWorkers, 10);
    if (!requiredWorkers.trim() || Number.isNaN(parsedRequiredWorkers) || parsedRequiredWorkers < 1) {
      errors.requiredWorkers = '募集人数は1名以上で入力してください';
    }
    const parsedSalaryAmount = Number.parseInt(broadcastSalaryAmount, 10);
    if (!broadcastSalaryAmount.trim() || Number.isNaN(parsedSalaryAmount) || parsedSalaryAmount <= 0) {
      errors.salaryAmount = '時給を入力してください';
    }
    // 手当・交通費は空欄NG。空欄で送るとシフトがNULLになり、表示側のテンプレ値フォールバックで
    // 以前の値が表示され続けるため、必ず明示的な値（なしの場合は0）を保存する
    const allowanceDigits = broadcastAllowance.replace(/[^\d]/g, '');
    if (!allowanceDigits) {
      errors.salaryAllowance = '手当を入力してください（なしの場合は0）';
    }
    const transportDigits = broadcastTransport.replace(/[^\d]/g, '');
    if (!transportDigits) {
      errors.salaryTransport = '交通費を入力してください（なしの場合は0）';
    }

    if (Object.keys(errors).length > 0 || !selectedTemplate) {
      setFieldErrors(errors);
      setError(null);
      scrollToField(Object.keys(errors)[0]);
      return;
    }

    setFieldErrors({});
    setError(null);
    try {
      const normalizedSalaryAmount = Number.isNaN(parsedSalaryAmount) ? undefined : parsedSalaryAmount;

      const shiftData: JobShiftCreate = {
        store_id: storeId,
        template_id: selectedTemplate.id,
        shift_date: workDate,
        start_time: startTime,
        end_time: endTime,
        salary_type: selectedTemplate.salary_type || 'hourly',
        salary_amount: normalizedSalaryAmount,
        salary_allowance: allowanceDigits,
        salary_transport: transportDigits,
        // この募集で編集した内容をシフトに保存する。送らないとひな形の値のまま扱われ、
        // 業務内容が労働条件通知書に反映されない（動作確認NG: 2-1 No.4）
        job_content: jobContent,
        business_content: workDescription,
        required_workers: parsedRequiredWorkers,
        break_duration_minutes: parseInt(breakDurationMinutes, 10) || 0,
        is_active: false,
        status: 'draft',
        smoking_prevention_measure_id: selectedSmokingMeasureId || undefined,
      };
      await jobShiftApi.create(shiftData, storeId);

      setToastMessage('下書きを保存しました');
      setToastSeverity('success');
      setOpenToast(true);
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      setError('下書きの保存に失敗しました');
    }
  };

  const handleSendMessage = async () => {
    if (!storeId) {
      setError('店舗IDが取得できませんでした');
      return;
    }

    // 入力バリデーション（インラインエラー）
    const errors: Record<string, string> = {};
    if (!selectedTemplate) {
      errors.template = 'ひな形を選択してください';
    }
    if (!messageContent.trim()) {
      errors.messageContent = 'メッセージを入力してください';
    }
    // ランク別配信で「全クルー」以外を選んだ場合、tier の選択が必須
    if (targetingMode === 'rank' && rankTargetMode !== 'all' && selectedTierIds.length === 0) {
      errors.tierIds = '配信対象のランクを1つ以上選択してください';
    }
    if (!workDate) {
      errors.workDate = '勤務日を入力してください';
    } else {
      // 勤務日が本日より前の場合はエラー
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(workDate);
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        errors.workDate = '勤務日は本日以降の日付を選択してください';
      }
    }
    const parsedRequiredWorkers = Number.parseInt(requiredWorkers, 10);
    if (!requiredWorkers.trim() || Number.isNaN(parsedRequiredWorkers) || parsedRequiredWorkers < 1) {
      errors.requiredWorkers = '募集人数は1名以上で入力してください';
    }
    const parsedSalaryAmount = Number.parseInt(broadcastSalaryAmount, 10);
    if (!broadcastSalaryAmount.trim() || Number.isNaN(parsedSalaryAmount) || parsedSalaryAmount <= 0) {
      errors.salaryAmount = '時給を入力してください';
    }
    // 手当・交通費は空欄NG。空欄で送るとシフトがNULLになり、表示側のテンプレ値フォールバックで
    // 以前の値が表示され続けるため、必ず明示的な値（なしの場合は0）を保存する
    const allowanceDigits = broadcastAllowance.replace(/[^\d]/g, '');
    if (!allowanceDigits) {
      errors.salaryAllowance = '手当を入力してください（なしの場合は0）';
    }
    const transportDigits = broadcastTransport.replace(/[^\d]/g, '');
    if (!transportDigits) {
      errors.salaryTransport = '交通費を入力してください（なしの場合は0）';
    }

    if (Object.keys(errors).length > 0 || !selectedTemplate) {
      setFieldErrors(errors);
      setError(null);
      scrollToField(Object.keys(errors)[0]);
      return;
    }

    setFieldErrors({});
    setError(null);
    setIsSending(true);

    try {
      const normalizedSalaryAmount = Number.isNaN(parsedSalaryAmount) ? undefined : parsedSalaryAmount;

      // シフトを作成してからLINE送信する
      const shiftData: JobShiftCreate = {
        store_id: storeId,
        template_id: selectedTemplate.id,
        shift_date: workDate,
        start_time: startTime,
        end_time: endTime,
        salary_type: selectedTemplate.salary_type || 'hourly',
        salary_amount: normalizedSalaryAmount,
        salary_allowance: allowanceDigits,
        salary_transport: transportDigits,
        // この募集で編集した内容をシフトに保存する。送らないとひな形の値のまま扱われ、
        // 業務内容が労働条件通知書に反映されない（動作確認NG: 2-1 No.4）
        job_content: jobContent,
        business_content: workDescription,
        required_workers: parsedRequiredWorkers,
        break_duration_minutes: parseInt(breakDurationMinutes, 10) || 0,
        is_active: true,
        smoking_prevention_measure_id: selectedSmokingMeasureId || undefined,
      };
      const createdShift = await jobShiftApi.create(shiftData, storeId);

      // メッセージは動的定型文(message)と手動入力(manual_text)を別フィールドで送信。
      // バックエンドが [動的定型文 → (複数シフト時)システム案内 → 手動テキスト] の順に組み立てる。
      let status: string;
      let recipientsCount: number;
      let resultErrorMessage: string | undefined;
      // 想定対象人数（トースト表示用）。ランク別はプレビュー値、セグメントはセグメント件数。
      let expectedCount: number;

      if (targetingMode === 'rank') {
        // スキルランク別配信（tier_ids + target_mode の構造化指定）
        const target = buildRankTarget();
        const byRankBody: BroadcastByRankRequest = {
          store_id: storeId,
          template_id: selectedTemplate.id,
          message: broadcastPreview,
          manual_text: messageContent.trim() || undefined,
          shift_id: createdShift.id,
          target_mode: target.target_mode,
          tier_ids: target.tier_ids,
          use_shifts: true,
          use_button: true,
          button_label: '詳細を見る',
        };
        const result = await broadcastApi.byRank(byRankBody, token);
        status = result.status || (result.recipients_count > 0 ? 'sent' : 'no_recipients');
        recipientsCount = result.recipients_count;
        resultErrorMessage = result.error_message;
        expectedCount = rankTargetCount ?? recipientsCount;
      } else {
        // 従来のセグメント配信
        const messageData: LineMessageSend = {
          template_id: selectedTemplate.id,
          message: broadcastPreview,
          manual_text: messageContent.trim() || undefined,
          target_segment: targetSegment,
          store_id: storeId,
          shift_id: createdShift.id,
          // オプションパラメータ
          use_shifts: true,        // シフト情報を使用する
          use_button: true,        // ボタンを使用する
          button_label: "詳細を見る"  // ボタンラベル
        };
        // APIを使用してメッセージを送信
        const result = await lineApi.sendMessage(messageData);
        status = result.status || (result.recipients_count > 0 ? 'sent' : 'no_recipients');
        recipientsCount = result.recipients_count;
        resultErrorMessage = result.error_message;
        expectedCount = getSegmentCount(targetSegment);
      }

      // トースト通知を表示
      const toastMsg = status === 'sent'
        ? `${expectedCount.toLocaleString()}人にメッセージを送信しました`
        : status === 'no_recipients'
          ? '対象となる受信者がいませんでした'
          : `送信に失敗しました。${resultErrorMessage ? resultErrorMessage : '詳細は配信履歴を確認してください'}`;

      const toastSev = status === 'sent' ? 'success' : status === 'no_recipients' ? 'warning' : 'error';

      setToastMessage(toastMsg);
      setToastSeverity(toastSev);
      setOpenToast(true);
    } catch (err: any) {
      // エラーメッセージをユーザーに表示（生の detail は連結しない）
      const errorMessage = getApiErrorMessage(err, 'メッセージの送信に失敗しました。');
      
      setError(errorMessage);
      
      // エラー通知もトーストで表示
      setToastMessage(errorMessage);
      setToastSeverity('error');
      setOpenToast(true);
    } finally {
      setIsSending(false);
    }
  };
  
  
  const getSegmentDescription = (segment: string): string => {
    switch (segment) {
      case 'all':
        return 'すべての登録者';
      case 'rankA_users':
        return '評価Aの登録者';
      case 'B_plus_users':
        return '評価B以上の登録者';
      default:
        return segment;
    }
  };

  const getSegmentCount = (segment: string): number => {
    return segmentCounts[segment] || 0;
  };

  // ─── 配信対象セクション（従来セグメント / スキルランク別の切替 + ランク別UI） ───
  // 既存のセグメント Radio はそのまま残し、ランク別オプションを上乗せする（§6.1 / §9-5）。
  const renderTargetingSection = () => (
    <>
      {/* 指定方式トグル: 評価セグメント / スキルランク別 */}
      <ToggleButtonGroup
        value={targetingMode}
        exclusive
        size="small"
        fullWidth
        onChange={(_e, v) => { if (v) setTargetingMode(v as 'segment' | 'rank'); }}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton
          value="segment"
          sx={{
            textTransform: 'none', fontWeight: 600,
            backgroundColor: 'rgba(0,0,0,0.08)', color: '#fff',
            '&.Mui-selected': { backgroundColor: '#fff', color: 'rgba(0,0,0,0.87)' },
            '&.Mui-selected:hover': { backgroundColor: '#f5f5f5' },
          }}
        >評価で選ぶ</ToggleButton>
        <ToggleButton
          value="rank"
          sx={{
            textTransform: 'none', fontWeight: 600,
            backgroundColor: 'rgba(0,0,0,0.08)', color: '#fff',
            '&.Mui-selected': { backgroundColor: '#fff', color: 'rgba(0,0,0,0.87)' },
            '&.Mui-selected:hover': { backgroundColor: '#f5f5f5' },
          }}
        >スキルランクで選ぶ</ToggleButton>
      </ToggleButtonGroup>

      {targetingMode === 'segment' ? (
        <RadioGroup
          aria-label="target-segment"
          name="target-segment"
          value={targetSegment}
          onChange={handleTargetSegmentChange}
          sx={{ color: 'text.tertiary' }}
        >
          {[
            { value: 'all', label: '全てのクルーに配信' },
            { value: 'rankA_users', label: '評価Aの登録者' },
            { value: 'B_plus_users', label: '評価B以上の登録者' },
          ].map((opt) => (
            <Box
              key={opt.value}
              component="label"
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1.5, borderRadius: 2, border: '1px solid',
                borderColor: targetSegment === opt.value ? '#0071e3' : '#e0e0e0',
                bgcolor: '#fff', cursor: 'pointer', mb: 1,
              }}
            >
              <Typography variant="body2" color="text.tertiary">{opt.label}</Typography>
              <Radio
                value={opt.value}
                size="small"
                sx={{ p: 0, '& .MuiSvgIcon-root': { color: targetSegment === opt.value ? '#0071e3' : '#e0e0e0' } }}
              />
            </Box>
          ))}
        </RadioGroup>
      ) : (
        <>
          {/* 配信モード: 全クルー / 指定ランクのみ / 指定ランク以上 */}
          <RadioGroup
            value={rankTargetMode}
            onChange={(e) => setRankTargetMode(e.target.value as BroadcastTargetMode)}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography variant="body2">全てのクルーに配信</Typography>} />
            <FormControlLabel value="tier_and_above" control={<Radio size="small" />} label={<Typography variant="body2">選択したランク以上に配信</Typography>} />
            <FormControlLabel value="tier" control={<Radio size="small" />} label={<Typography variant="body2">選択したランクのみに配信</Typography>} />
          </RadioGroup>

          {/* tier チェックボックス（色付き星）。全クルー時は選択不要 */}
          <Box
            sx={{
              opacity: rankTargetMode === 'all' ? 0.5 : 1,
              pointerEvents: rankTargetMode === 'all' ? 'none' : 'auto',
            }}
          >
            {skillTiers.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
                スキルランクが設定されていません。
              </Typography>
            ) : (
              skillTiers.map((tier) => {
                const checked = selectedTierIds.includes(tier.tier_id);
                return (
                  <Box
                    key={tier.tier_id}
                    component="label"
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.5, py: 1, borderRadius: 2, border: '1px solid',
                      borderColor: checked ? '#0071e3' : '#e0e0e0',
                      bgcolor: '#fff', cursor: 'pointer', mb: 1,
                    }}
                  >
                    <RankStar tierCode={tier.code} color={tier.color} label={tier.name} showLabel size={18} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {loadingTierCounts ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {tierCounts[tier.tier_id] !== undefined ? `${tierCounts[tier.tier_id].toLocaleString()}人` : '-'}
                        </Typography>
                      )}
                      <Checkbox
                        checked={checked}
                        onChange={() => { toggleTierId(tier.tier_id); clearFieldError('tierIds'); }}
                        size="small"
                        sx={{ p: 0, '&.Mui-checked': { color: '#0071e3' } }}
                      />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {fieldErrors.tierIds && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {fieldErrors.tierIds}
            </Typography>
          )}

          {/* 対象人数プレビュー */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.25, borderRadius: 2, bgcolor: '#f0f7ff', border: '1px solid #cce3f9',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3c3c3c' }}>
              配信対象人数
            </Typography>
            {loadingRankCount ? (
              <CircularProgress size={18} />
            ) : (
              <Typography sx={{ fontWeight: 700, color: '#0071e3' }}>
                {(rankTargetCount ?? 0).toLocaleString()}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  人
                </Typography>
              </Typography>
            )}
          </Box>
        </>
      )}
    </>
  );

  // モバイル表示
  const renderMobileView = () => (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {templateError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templateError}
        </Alert>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 1, bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}>
            <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                求人ひな形
              </Typography>
              {/* ひな形0件では配信できない（ひな形の選択が必須）。
                  プルダウンが空なだけだと理由が分からないため、作成導線を明示する。 */}
              {jobTemplates.length === 0 ? (
                <Alert
                  severity="info"
                  sx={{ mb: 1.5 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setShowTemplateDialog(true)}
                    >
                      ひな形を作成
                    </Button>
                  }
                >
                  求人ひな形が登録されていません。募集を配信するには、まずひな形を作成してください。
                </Alert>
              ) : (
              <FormControl fullWidth sx={{ mb: 1.5 }} error={!!fieldErrors.template}>
                <Select
                  name="template"
                  value={selectedTemplate ? selectedTemplate.id.toString() : ''}
                  onChange={(e) => { handleTemplateSelectChange(e); clearFieldError('template'); }}
                  disabled={isLoading || isSending}
                  displayEmpty
                  error={!!fieldErrors.template}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fff',
                    color: 'text.tertiary',
                  }}
                >
                  <MenuItem value="" disabled>選択してください</MenuItem>
                  {jobTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id.toString()}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.template && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                    {fieldErrors.template}
                  </Typography>
                )}
              </FormControl>
              )}

              {selectedTemplate && (
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    {selectedTemplate.tier_code && (
                      <Box sx={{ mb: 1 }}>
                        <RankStar tierCode={selectedTemplate.tier_code} size={20} />
                      </Box>
                    )}
                    <Typography variant="h6" color="text.tertiary" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                      {selectedTemplate.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1.5 }}
                    >
                      {selectedTemplate.admin_label || '募集メモなし'}
                    </Typography>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px' }}>雇用形態</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedTemplate.job_type}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px', flexShrink: 0 }}>報酬</Typography>
                      <Box sx={{ flex: 1 }}>
                        {selectedTemplate.salary_amount && (
                          <Typography variant="body2" sx={{ mb: 0.3 }}>
                            {selectedTemplate.salary_type === 'hourly' ? '時給' : '日給'}:{selectedTemplate.salary_amount.toLocaleString()}円
                          </Typography>
                        )}
                        {selectedTemplate.salary_allowance && (
                          <Typography variant="body2" sx={{ mb: 0.3 }}>
                            手当:{/^\d+$/.test(selectedTemplate.salary_allowance)
                              ? `${parseInt(selectedTemplate.salary_allowance, 10).toLocaleString()}円`
                              : selectedTemplate.salary_allowance}
                          </Typography>
                        )}
                        {selectedTemplate.salary_transport && (
                          <Typography variant="body2" sx={{ mb: 0.3 }}>
                            交通費:{/^\d+$/.test(selectedTemplate.salary_transport)
                              ? `${parseInt(selectedTemplate.salary_transport, 10).toLocaleString()}円`
                              : selectedTemplate.salary_transport}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Typography variant="body2" color="text.tertiary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                配信対象
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                評価は「クルー管理」の画面からワーカー毎に設定することができます。
              </Typography>
              {renderTargetingSection()}

            </CardContent>
          </Card>

          {/* 勤務日・時間・仕事内容（モバイル） */}
          <Card sx={{ mb: 1, bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}>
            <CardContent sx={{ px: 0, pt: 0, '&:last-child': { pb: 0 } }}>
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                勤務日
              </Typography>
              <TextField
                fullWidth
                type="date"
                name="workDate"
                value={workDate}
                onChange={(e) => { setWorkDate(e.target.value); clearFieldError('workDate'); }}
                error={!!fieldErrors.workDate}
                helperText={fieldErrors.workDate}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                    開始時間
                  </Typography>
                  <TimeSelect
                    fullWidth
                    value={startTime}
                    onChange={(v) => setStartTime(v)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                    終了時間
                  </Typography>
                  <TimeSelect
                    fullWidth
                    value={endTime}
                    onChange={(v) => setEndTime(v)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                募集人数
              </Typography>
              <TextField
                fullWidth
                name="requiredWorkers"
                value={requiredWorkers}
                onChange={(e) => { setRequiredWorkers(e.target.value.replace(/\D/g, '')); clearFieldError('requiredWorkers'); }}
                error={!!fieldErrors.requiredWorkers}
                helperText={fieldErrors.requiredWorkers}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                休憩時間（分）
              </Typography>
              <TextField
                fullWidth
                value={breakDurationMinutes}
                onChange={(e) => setBreakDurationMinutes(e.target.value.replace(/\D/g, ''))}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                placeholder="0"
              />
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                受動喫煙防止措置
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={selectedSmokingMeasureId}
                  onChange={(e: SelectChangeEvent) => setSelectedSmokingMeasureId(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2, bgcolor: '#fff' }}
                >
                  <MenuItem value="">指定なし</MenuItem>
                  {smokingMeasures.map((measure) => (
                    <MenuItem key={measure.id} value={measure.id}>
                      {measure.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                仕事内容（求人用）
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={jobContent}
                onChange={(e) => setJobContent(e.target.value)}
                placeholder="求職者向けの仕事内容を記載してください。LINE配信やWEB掲載時に使用されます。"
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
              />
              <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                業務内容（労働条件通知書）
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                placeholder="労働条件通知書に記載する詳細な業務内容"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
              />
            </CardContent>
          </Card>

          {/* LINE配信メッセージ */}
          <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
            LINE配信メッセージ
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', lineHeight: 1.6 }}>
            職種・報酬・日時・募集人数は自動表示されます。配信に追記したいメッセージを入力してください。自動表示含めて15行以上の場合、配信時に表示しきれない場合があります。
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            name="messageContent"
            value={messageContent}
            onChange={(e) => { setMessageContent(e.target.value); clearFieldError('messageContent'); }}
            error={!!fieldErrors.messageContent}
            helperText={fieldErrors.messageContent}
            disabled={isSending}
            placeholder="LINE配信時に使用するメッセージを記載してください。"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
          />

          {/* LINE配信メッセージ プレビュー（手動テキスト＋動的定型文の結合表示） */}
          {selectedTemplate && broadcastPreview && (
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e7e7e7', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                LINE配信メッセージ プレビュー
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.8 }}>
                {fullBroadcastPreview}
              </Typography>
            </Box>
          )}

          {/* 給与・手当 */}
          <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
            給与・手当
          </Typography>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e7e7e7', bgcolor: '#fff' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>時給</Typography>
                  <TextField
                    name="salaryAmount"
                    type="text"
                    value={formatWithCommasBroadcast(broadcastSalaryAmount)}
                    onChange={(e) => { setBroadcastSalaryAmount(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryAmount'); }}
                    error={!!fieldErrors.salaryAmount}
                    inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                    sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                  />
                  <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                </Box>
                {fieldErrors.salaryAmount && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                    {fieldErrors.salaryAmount}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>手当</Typography>
                  <TextField
                    name="salaryAllowance"
                    type="text"
                    value={formatWithCommasBroadcast(broadcastAllowance)}
                    onChange={(e) => { setBroadcastAllowance(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryAllowance'); }}
                    error={!!fieldErrors.salaryAllowance}
                    inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                    sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                  />
                  <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                </Box>
                {fieldErrors.salaryAllowance && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                    {fieldErrors.salaryAllowance}
                  </Typography>
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>交通費</Typography>
                  <TextField
                    name="salaryTransport"
                    type="text"
                    value={formatWithCommasBroadcast(broadcastTransport)}
                    onChange={(e) => { setBroadcastTransport(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryTransport'); }}
                    error={!!fieldErrors.salaryTransport}
                    inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                    sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                  />
                  <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                </Box>
                {fieldErrors.salaryTransport && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                    {fieldErrors.salaryTransport}
                  </Typography>
                )}
              </Box>
              {(() => {
                const total = calcTotalPayment(
                  parseInt(broadcastSalaryAmount, 10) || undefined,
                  selectedTemplate?.salary_type,
                  startTime,
                  endTime,
                  parseInt(breakDurationMinutes, 10) || 0,
                  broadcastAllowance,
                  broadcastTransport
                );
                if (total === null) return null;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e7e7e7' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: '56px' }}>合計</Typography>
                    <Typography variant="body1" fontWeight={700} color="#e07800" sx={{ ml: 'auto' }}>
                      ¥{total.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>

          {/* ボタン */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleSaveDraft}
              sx={{
                flex: 1, borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: '0.85rem',
                whiteSpace: 'nowrap', minWidth: 0,
                bgcolor: '#cce3f9', color: '#0071e3', boxShadow: 'none',
                '&:hover': { bgcolor: '#b8d8f7', boxShadow: 'none' },
              }}
            >
              下書きを保存
            </Button>
            <Button
              variant="contained"
              disabled={!selectedTemplate || !messageContent.trim() || isSending}
              onClick={handleSendMessage}
              sx={{ flex: 2, borderRadius: 2, py: 1.2, fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', minWidth: 0, bgcolor: '#0071e3', '&:hover': { bgcolor: '#0062c4' } }}
            >
              {isSending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  送信中...
                </>
              ) : 'LINEで募集開始'}
            </Button>
          </Box>
        </>
      )}
    </>
  );

  // デスクトップ表示
  const renderDesktopView = () => (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {templateError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templateError}
        </Alert>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ mb: 1, bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}>
              <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  求人ひな形
                </Typography>
                {/* ひな形0件では配信できない（ひな形の選択が必須）。作成導線を明示する。 */}
                {jobTemplates.length === 0 ? (
                  <Alert
                    severity="info"
                    sx={{ mb: 1.5 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setShowTemplateDialog(true)}
                      >
                        ひな形を作成
                      </Button>
                    }
                  >
                    求人ひな形が登録されていません。募集を配信するには、まずひな形を作成してください。
                  </Alert>
                ) : (
                <FormControl fullWidth sx={{ mb: 1.5 }} error={!!fieldErrors.template}>
                  <Select
                    name="template"
                    value={selectedTemplate ? selectedTemplate.id.toString() : ''}
                    onChange={(e) => { handleTemplateSelectChange(e); clearFieldError('template'); }}
                    disabled={isLoading || isSending}
                    displayEmpty
                    error={!!fieldErrors.template}
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#fff',
                      color: 'text.tertiary',
                    }}
                  >
                    <MenuItem value="" disabled>選択してください</MenuItem>
                    {jobTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id.toString()}>
                        {template.name} - {template.job_title}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.template && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                      {fieldErrors.template}
                    </Typography>
                  )}
                </FormControl>
                )}

                {selectedTemplate && (
                  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {selectedTemplate.tier_code && (
                        <Box sx={{ mb: 1 }}>
                          <RankStar tierCode={selectedTemplate.tier_code} size={20} />
                        </Box>
                      )}
                      <Typography variant="h6" color="text.tertiary" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                        {selectedTemplate.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1.5 }}
                      >
                        {selectedTemplate.admin_label || '募集メモなし'}
                      </Typography>
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px' }}>雇用形態</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedTemplate.job_type}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px', flexShrink: 0 }}>報酬</Typography>
                        <Box sx={{ flex: 1 }}>
                          {selectedTemplate.salary_amount && (
                            <Typography variant="body2" sx={{ mb: 0.3 }}>
                              {selectedTemplate.salary_type === 'hourly' ? '時給' : '日給'}:{selectedTemplate.salary_amount.toLocaleString()}円
                            </Typography>
                          )}
                          {selectedTemplate.salary_allowance && (
                            <Typography variant="body2" sx={{ mb: 0.3 }}>
                              手当:{/^\d+$/.test(selectedTemplate.salary_allowance)
                                ? `${parseInt(selectedTemplate.salary_allowance, 10).toLocaleString()}円`
                                : selectedTemplate.salary_allowance}
                            </Typography>
                          )}
                          {selectedTemplate.salary_transport && (
                            <Typography variant="body2" sx={{ mb: 0.3 }}>
                              交通費:{/^\d+$/.test(selectedTemplate.salary_transport)
                                ? `${parseInt(selectedTemplate.salary_transport, 10).toLocaleString()}円`
                                : selectedTemplate.salary_transport}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Typography variant="body2" color="text.tertiary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  配信対象
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  評価は「クルー管理」の画面からワーカー毎に設定することができます。
                </Typography>
                {renderTargetingSection()}
              </CardContent>
            </Card>

            {/* 勤務日・時間・仕事内容（デスクトップ） */}
            <Card sx={{ mb: 3, bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}>
              <CardContent sx={{ px: 0 }}>
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  勤務日
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  name="workDate"
                  value={workDate}
                  onChange={(e) => { setWorkDate(e.target.value); clearFieldError('workDate'); }}
                  error={!!fieldErrors.workDate}
                  helperText={fieldErrors.workDate}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                  InputLabelProps={{ shrink: true }}
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                      開始時間
                    </Typography>
                    <TimeSelect
                      fullWidth
                      value={startTime}
                      onChange={(v) => setStartTime(v)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                      終了時間
                    </Typography>
                    <TimeSelect
                      fullWidth
                      value={endTime}
                      onChange={(v) => setEndTime(v)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  募集人数
                </Typography>
                <TextField
                  fullWidth
                  name="requiredWorkers"
                  value={requiredWorkers}
                  onChange={(e) => { setRequiredWorkers(e.target.value.replace(/\D/g, '')); clearFieldError('requiredWorkers'); }}
                  error={!!fieldErrors.requiredWorkers}
                  helperText={fieldErrors.requiredWorkers}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  休憩時間（分）
                </Typography>
                <TextField
                  fullWidth
                  value={breakDurationMinutes}
                  onChange={(e) => setBreakDurationMinutes(e.target.value.replace(/\D/g, ''))}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  placeholder="0"
                />
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  受動喫煙防止措置
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={selectedSmokingMeasureId}
                    onChange={(e: SelectChangeEvent) => setSelectedSmokingMeasureId(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 2, bgcolor: '#fff' }}
                  >
                    <MenuItem value="">指定なし</MenuItem>
                    {smokingMeasures.map((measure) => (
                      <MenuItem key={measure.id} value={measure.id}>
                        {measure.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  仕事内容（求人用）
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={jobContent}
                  onChange={(e) => setJobContent(e.target.value)}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                />
                <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
                  業務内容（労働条件通知書）
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={workDescription}
                  onChange={(e) => setWorkDescription(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
                />
              </CardContent>
            </Card>

          </Grid>

          <Grid item xs={12} md={7}>
            {/* LINE配信メッセージ */}
            <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, pt: 2, display: 'block', fontWeight: 500 }}>
              LINE配信メッセージ
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', lineHeight: 1.6 }}>
              職種・報酬・日時・募集人数は自動表示されます。配信に追記したいメッセージを入力してください。自動表示含めて15行以上の場合、配信時に表示しきれない場合があります。
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              name="messageContent"
              value={messageContent}
              onChange={(e) => { setMessageContent(e.target.value); clearFieldError('messageContent'); }}
              error={!!fieldErrors.messageContent}
              helperText={fieldErrors.messageContent}
              disabled={isSending}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', color: 'text.tertiary' } }}
            />

            {/* LINE配信メッセージ プレビュー（手動テキスト＋動的定型文の結合表示） */}
            {selectedTemplate && broadcastPreview && (
              <Box sx={{ mb: 3, p: 2, border: '1px solid #e7e7e7', borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  LINE配信メッセージ プレビュー
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.8 }}>
                  {fullBroadcastPreview}
                </Typography>
              </Box>
            )}

            {/* 給与・手当 */}
            <Typography variant="body2" color="text.tertiary" sx={{ mb: 0.75, display: 'block', fontWeight: 500 }}>
              給与・手当
            </Typography>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 'none', border: '1px solid #e7e7e7', bgcolor: '#fff' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>時給</Typography>
                    <TextField
                      name="salaryAmount"
                      type="text"
                      value={formatWithCommasBroadcast(broadcastSalaryAmount)}
                      onChange={(e) => { setBroadcastSalaryAmount(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryAmount'); }}
                      error={!!fieldErrors.salaryAmount}
                      inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                      sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                    />
                    <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                  </Box>
                  {fieldErrors.salaryAmount && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                      {fieldErrors.salaryAmount}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>手当</Typography>
                    <TextField
                      name="salaryAllowance"
                      type="text"
                      value={formatWithCommasBroadcast(broadcastAllowance)}
                      onChange={(e) => { setBroadcastAllowance(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryAllowance'); }}
                      error={!!fieldErrors.salaryAllowance}
                      inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                      sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                    />
                    <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                  </Box>
                  {fieldErrors.salaryAllowance && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                      {fieldErrors.salaryAllowance}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.tertiary" sx={{ minWidth: '56px' }}>交通費</Typography>
                    <TextField
                      name="salaryTransport"
                      type="text"
                      value={formatWithCommasBroadcast(broadcastTransport)}
                      onChange={(e) => { setBroadcastTransport(e.target.value.replace(/[^\d]/g, '')); clearFieldError('salaryTransport'); }}
                      error={!!fieldErrors.salaryTransport}
                      inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                      sx={{ ml: 'auto', width: '280px', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f5', color: 'text.tertiary' } }}
                    />
                    <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                  </Box>
                  {fieldErrors.salaryTransport && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                      {fieldErrors.salaryTransport}
                    </Typography>
                  )}
                </Box>
                {(() => {
                  const total = calcTotalPayment(
                    parseInt(broadcastSalaryAmount, 10) || undefined,
                    selectedTemplate?.salary_type,
                    startTime,
                    endTime,
                    parseInt(breakDurationMinutes, 10) || 0,
                    broadcastAllowance,
                    broadcastTransport
                  );
                  if (total === null) return null;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e7e7e7' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: '56px' }}>合計</Typography>
                      <Typography variant="body1" fontWeight={700} color="#e07800" sx={{ ml: 'auto' }}>
                        ¥{total.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.tertiary" sx={{ ml: 1, flexShrink: 0 }}>円</Typography>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            {/* ボタン */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleSaveDraft}
                sx={{
                  flex: 1, borderRadius: 2, py: 1.5, fontWeight: 600,
                  whiteSpace: 'nowrap', minWidth: 0,
                  bgcolor: '#cce3f9', color: '#0071e3', boxShadow: 'none',
                  '&:hover': { bgcolor: '#b8d8f7', boxShadow: 'none' },
                }}
              >
                下書きを保存
              </Button>
              <Button
                variant="contained"
                disabled={!selectedTemplate || !messageContent.trim() || isSending}
                onClick={handleSendMessage}
                sx={{ flex: 2, borderRadius: 2, py: 1.5, fontWeight: 600, whiteSpace: 'nowrap', minWidth: 0, bgcolor: '#0071e3', '&:hover': { bgcolor: '#0062c4' } }}
              >
                {isSending ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    送信中...
                  </>
                ) : 'LINEで募集開始'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      <PageHeader
        title="今すぐ募集"
        icon={<RecruitFabIcon style={{ width: 22, height: 22, fill: '#898989' }} />}
      />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>

        {/* 新規ひな形作成ボタン（タイトル直下・全幅） */}
        <Button
          fullWidth
          onClick={handleCreateTemplate}
          disabled={isLoading || loadingStoreInfo || loadingJobPositions}
          sx={{
            mb: 0,
            bgcolor: '#fff',
            color: '#0071e3',
            border: '2px solid #bfdbfe',
            borderRadius: '10px',
            py: 0.5,
            fontWeight: 600,
            fontSize: '0.9rem',
            boxShadow: 'none',
            justifyContent: 'center',
            textAlign: 'center',
            '&:hover': {
              bgcolor: '#f0f7ff',
              border: '2px solid #bfdbfe',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: '#fff',
              color: '#aaa',
              border: '2px solid #e0e0e0',
            },
          }}
        >
          新規ひな形を作成する
        </Button>

      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* テンプレート作成ダイアログ */}
      <TemplateDialog
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        onSave={handleSaveTemplate}
        isLoading={isCreatingTemplate}
        error={templateError}
        storeAddress={storeAddress}
        jobPositions={jobPositions}
        loadingJobPositions={loadingJobPositions}
      />

      
      {/* トースト通知 */}
      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseToast}
          severity={toastSeverity}
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default LineBroadcast;